/**
 * CircularGauge Component
 *
 * Full-circle gauge for displaying scores with configurable sizes.
 * Color thresholds follow the shared audit score bands:
 * strong green ≥80, brand green ≥60, orange ≥40, red <40.
 */
import { getAuditScoreColorVar, getAuditScoreTextClass } from "./scoreBands";

export type CircularGaugeSize = "sm" | "md" | "lg";

export interface CircularGaugeProps {
  /** Score value (0-100) */
  score: number;
  /** Size preset: sm (48px), md (80px), lg (120px) */
  size?: CircularGaugeSize;
  /** Whether to show the score label */
  showLabel?: boolean;
  /** Optional subtitle text below the score */
  subtitle?: string;
}

const sizeConfig = {
  sm: {
    diameter: 48,
    strokeWidth: 4,
    fontSize: "text-sm",
    fontWeight: "font-bold",
    subtitleSize: "text-[8px]",
  },
  md: {
    diameter: 80,
    strokeWidth: 6,
    fontSize: "text-xl",
    fontWeight: "font-bold",
    subtitleSize: "text-xs",
  },
  lg: {
    diameter: 120,
    strokeWidth: 8,
    fontSize: "text-3xl",
    fontWeight: "font-black",
    subtitleSize: "text-sm",
  },
};

export function CircularGauge({
  score,
  size = "md",
  showLabel = true,
  subtitle,
}: CircularGaugeProps) {
  const config = sizeConfig[size];
  const { diameter, strokeWidth } = config;
  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const strokeColor = getAuditScoreColorVar(score, "var(--color-sev-critical)");
  const textClass = getAuditScoreTextClass(score, "text-destructive");

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={diameter}
        height={diameter}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-zinc-700"
        />
        {/* Score arc */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
      </svg>
      {/* Center content */}
      {showLabel && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-accent ${config.fontSize} ${config.fontWeight} ${textClass}`}>
            {score}
          </span>
          {subtitle && (
            <span className={`${config.subtitleSize} text-gray-500 dark:text-gray-400`}>
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
